import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import Loader from '../components/Loader';
import SuccessModal from '../components/SuccessModal';
import { motion } from 'framer-motion';
import {
  FaUserMd, FaCalendarAlt, FaStar, FaCheck, FaTimes,
  FaSearch, FaClipboardCheck, FaUserGraduate, FaSignature,
  FaListAlt, FaCheckCircle, FaUser, FaTooth, FaFileMedical,
  FaNotesMedical, FaImages, FaFlask, FaFileSignature, FaClipboardList,
  FaPhone, FaMapMarkerAlt, FaBriefcase, FaVenusMars, FaBirthdayCake,
  FaIdCard, FaExternalLinkAlt, FaDownload, FaEye
} from 'react-icons/fa';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Import custom components
import SignatureManager from './SignatureManager';
import ReviewStepsDisplay from './ReviewStepsDisplay';
import { patientNationalId } from '../utils/patientRef';

// Website color palette
const colorPalette = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  background: '#FFFFFF',
  text: '#333333',
  accent: '#28A745'
};

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Chart.js default configuration
ChartJS.defaults.font.family = 'Inter, system-ui, sans-serif';
ChartJS.defaults.font.size = 12;
ChartJS.defaults.color = '#6b7280';
ChartJS.defaults.plugins.tooltip.backgroundColor = 'rgba(0, 0, 0, 0.8)';
ChartJS.defaults.plugins.tooltip.titleColor = '#ffffff';
ChartJS.defaults.plugins.tooltip.bodyColor = '#ffffff';
ChartJS.defaults.plugins.legend.labels.usePointStyle = true;
ChartJS.defaults.plugins.legend.labels.padding = 20;

// Helper function to parse Firestore dates
const parseFirestoreDate = (dateValue) => {
  try {
    if (!dateValue) return null;
    
    // Handle Firestore timestamp objects with toDate method
    if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
      return dateValue.toDate();
    }
    
    // Handle Firestore timestamp objects with _seconds
    if (dateValue && typeof dateValue === 'object' && dateValue._seconds) {
      return new Date(dateValue._seconds * 1000);
    }
    
    // Handle regular Date objects or date strings
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    if (typeof dateValue === 'string') {
      return new Date(dateValue);
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

// Helper function to format dates for display
const formatDateForDisplay = (dateValue) => {
  try {
    const date = parseFirestoreDate(dateValue);
    if (!date || isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

// Helper function to format date and time for display
const formatDateTimeForDisplay = (dateValue) => {
  try {
    const date = parseFirestoreDate(dateValue);
    if (!date || isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

// Helper function to format detail values for display (human-readable) - simple values only
const formatSimpleValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    if (value._seconds || value.seconds) return formatDateForDisplay(value);
    if (Array.isArray(value)) return value.length === 0 ? 'None' : value.join(', ');
  }
  return String(value);
};

// Helper function to format detail values for display (human-readable)
const formatDetailValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    return value.map(v => typeof v === 'object' ? formatDetailValue(v) : String(v)).join(', ');
  }
  if (typeof value === 'object') {
    if (value._seconds || value.seconds) return formatDateForDisplay(value);
    return 'See details below';
  }
  return String(value) || 'N/A';
};

// Helper function to check if value is a complex nested object
const isComplexObject = (value) => {
  if (typeof value !== 'object' || value === null) return false;
  if (Array.isArray(value)) return false;
  if (value._seconds || value.seconds) return false;
  return Object.keys(value).length > 0;
};

// Helper function to flatten nested object for display
const flattenObjectForDisplay = (obj, prefix = '') => {
  const result = [];
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined || value === '') continue;
    const displayKey = formatKeyName(key);
    
    if (isComplexObject(value)) {
      result.push(...flattenObjectForDisplay(value, prefix ? `${prefix} > ${displayKey}` : displayKey));
    } else {
      result.push({
        key: prefix ? `${prefix} > ${displayKey}` : displayKey,
        value: formatSimpleValue(value)
      });
    }
  }
  return result;
};

// Helper function to format key names for display
const formatKeyName = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

const SupervisorDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [allReviews, setAllReviews] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [doneReviews, setDoneReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewsTab, setReviewsTab] = useState('pending');
  const [modalTab, setModalTab] = useState('details');
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState('week');
  const [savedSignature, setSavedSignature] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({});
  const [stepStatuses, setStepStatuses] = useState({});
  
  // Patient Info Modal State
  const [showPatientInfoModal, setShowPatientInfoModal] = useState(false);
  const [patientInfoTab, setPatientInfoTab] = useState('overview');
  const [patientFullData, setPatientFullData] = useState(null);
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [patientTeethCharts, setPatientTeethCharts] = useState([]);
  const [patientLabRequests, setPatientLabRequests] = useState([]);
  const [loadingPatientData, setLoadingPatientData] = useState(false);
  
  // Detail modals
  const [selectedChart, setSelectedChart] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null);
  
  // Review form
  const [reviewForm, setReviewForm] = useState({
    comment: '',
    supervisorSignature: null
  });

  // Filters
  const [studentFilter, setStudentFilter] = useState('');
  const [procedureFilter, setProcedureFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id || !token || user.role !== 'supervisor') {
        setError('Please log in as a supervisor to view dashboard.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const config = { headers: { Authorization: `Bearer ${token}` } };

        // Fetch all reviews for the supervisor
        const allReviewsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/reviews/supervisor`, config);
        const reviews = Array.isArray(allReviewsRes.data) ? allReviewsRes.data : [];
        setAllReviews(reviews);

        // Separate reviews by status
        setPendingReviews(reviews.filter(r => r.status === 'pending'));
        setDoneReviews(reviews.filter(r => r.status !== 'pending'));

        // Fetch supervisor's saved signature
        try {
          const signatureRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/supervisors/signature`, config);
          if (signatureRes.data && signatureRes.data.signature) {
            setSavedSignature(signatureRes.data.signature);
          }
        } catch (signatureErr) {
          // Non-critical error, continue without signature
        }

        // Calculate analytics
        calculateAnalytics(reviews);
        setLoading(false);
      } catch (err) {
        const errorMessage = err.response?.data?.message ||
                            (err.response?.status === 403 ? 'Access denied: Insufficient permissions' :
                            'Failed to load reviews. Please try again.');
        setError(errorMessage);
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        }
        setPendingReviews([]);
        setDoneReviews([]);
        setAllReviews([]);
        setLoading(false);
      }
    };

    fetchData();
  }, [user, token, navigate, analyticsTimeRange]);

  const calculateAnalytics = (reviews) => {
    const total = reviews.length;
    const accepted = reviews.filter(r => r.status === 'accepted').length;
    const declined = reviews.filter(r => r.status === 'denied').length;
    const pending = reviews.filter(r => r.status === 'pending').length;

    setAnalyticsData({
      totalReviews: total,
      statusDistribution: { accepted, declined, pending }
    });
  };

  const handleReviewSelect = (review) => {
    setSelectedReview(review);
    setReviewForm({
      comment: review.comment || '',
      supervisorSignature: savedSignature
    });
    
    // Initialize step statuses
    if (review.stepStatuses) {
      setStepStatuses(review.stepStatuses);
    }
  };

  const handleReviewSubmit = async (reviewId, status) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const updatedReview = {
        comment: reviewForm.comment || '',
        status: status,
        supervisorSignature: reviewForm.supervisorSignature,
        stepStatuses: stepStatuses,
        reviewedDate: new Date()
      };

      console.log('Submitting review:', updatedReview);
      await axios.put(`${process.env.REACT_APP_API_URL}/api/reviews/${reviewId}`, updatedReview, config);

      // Update local state
      setAllReviews(prev => prev.map(r => r.id === reviewId ? { ...r, ...updatedReview } : r));
      setPendingReviews(prev => prev.filter(r => r.id !== reviewId));
      setDoneReviews(prev => [...prev, { ...selectedReview, ...updatedReview }]);
      
      setSelectedReview(null);
      setSuccessMessage(`Review ${status === 'accepted' ? 'accepted' : 'declined'} successfully!`);
      setShowSuccessModal(true);
      
      setTimeout(() => setShowSuccessModal(false), 3000);
    } catch (err) {
      console.error('Review submission error:', err);
      setError(err.response?.data?.message || `Failed to submit review: ${err.message}`);
    }
  };

  const handleStepStatusChange = (stepId, status) => {
    setStepStatuses(prev => ({
      ...prev,
      [stepId]: status
    }));
  };

  // Fetch full patient information
  const fetchPatientFullInfo = async (nationalId) => {
    if (!nationalId) {
      console.error('No nationalId provided');
      return;
    }
    
    console.log('Fetching patient info for:', nationalId);
    setLoadingPatientData(true);
    setPatientInfoTab('overview');
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // First, try to get patient data from supervisor endpoint
      let patientData = null;
      try {
        const patientRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/supervisors/patient/${nationalId}`, config);
        patientData = patientRes.data;
        console.log('Patient data fetched from supervisor endpoint:', patientData);
      } catch (patientErr) {
        console.error('Failed to fetch patient from supervisor endpoint:', patientErr);
        // Try the general patients endpoint
        try {
          const patientRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/patients/${nationalId}`, config);
          patientData = patientRes.data;
          console.log('Patient data fetched from patients endpoint:', patientData);
        } catch (patientErr2) {
          console.error('Failed to fetch patient from patients endpoint:', patientErr2);
          // If patient fetch fails, use the data from selectedReview
          if (selectedReview?.patientId) {
            patientData = {
              nationalId: selectedReview.patientId.nationalId,
              fullName: selectedReview.patientId.fullName || 'Unknown Patient',
              phoneNumber: selectedReview.patientId.phoneNumber || 'N/A',
              gender: selectedReview.patientId.gender || 'N/A',
              age: selectedReview.patientId.age || 'N/A',
              address: selectedReview.patientId.address || 'N/A',
              occupation: selectedReview.patientId.occupation || 'N/A',
              medicalInfo: selectedReview.patientId.medicalInfo || {},
              treatmentSheets: [],
              xrays: [],
              galleryImages: [],
              consent: {}
            };
            console.log('Using fallback patient data from review:', patientData);
          }
        }
      }
      
      if (!patientData) {
        setError('Could not load patient information');
        setLoadingPatientData(false);
        return;
      }
      
      setPatientFullData(patientData);
      
      // Fetch additional data in parallel using supervisor-specific endpoints
      const [appointmentsRes, teethChartsRes, labRequestsRes, treatmentSheetsRes] = await Promise.allSettled([
        axios.get(`${process.env.REACT_APP_API_URL}/api/appointments/patient/${nationalId}`, config),
        axios.get(`${process.env.REACT_APP_API_URL}/api/dental/patient/${nationalId}`, config),
        axios.get(`${process.env.REACT_APP_API_URL}/api/supervisors/patient/${nationalId}/lab-requests`, config),
        axios.get(`${process.env.REACT_APP_API_URL}/api/supervisors/patient/${nationalId}/treatment-sheets`, config)
      ]);
      
      // Set appointments
      if (appointmentsRes.status === 'fulfilled') {
        const appts = Array.isArray(appointmentsRes.value.data) ? appointmentsRes.value.data : [];
        console.log('Appointments fetched:', appts.length);
        setPatientAppointments(appts);
      } else {
        console.log('Appointments fetch failed:', appointmentsRes.reason);
        setPatientAppointments([]);
      }
      
      // Set teeth charts
      if (teethChartsRes.status === 'fulfilled') {
        const charts = Array.isArray(teethChartsRes.value.data) ? teethChartsRes.value.data : [];
        console.log('Teeth charts fetched:', charts.length);
        setPatientTeethCharts(charts);
      } else {
        console.log('Teeth charts fetch failed:', teethChartsRes.reason);
        setPatientTeethCharts([]);
      }
      
      // Set lab requests from supervisor endpoint
      if (labRequestsRes.status === 'fulfilled') {
        const labReqs = Array.isArray(labRequestsRes.value.data) ? labRequestsRes.value.data : [];
        console.log('Lab requests fetched:', labReqs.length);
        setPatientLabRequests(labReqs);
      } else {
        console.log('Lab requests fetch failed:', labRequestsRes.reason);
        setPatientLabRequests([]);
      }
      
      // Update patient data with treatment sheets if fetched separately
      if (treatmentSheetsRes.status === 'fulfilled') {
        const sheets = Array.isArray(treatmentSheetsRes.value.data) ? treatmentSheetsRes.value.data : [];
        console.log('Treatment sheets fetched:', sheets.length);
        // Update patientFullData with fetched treatment sheets
        setPatientFullData(prev => ({
          ...prev,
          treatmentSheets: sheets.length > 0 ? sheets : (prev?.treatmentSheets || [])
        }));
      } else {
        console.log('Treatment sheets fetch failed:', treatmentSheetsRes.reason);
      }
      
      // Show the modal
      console.log('Opening patient info modal');
      setShowPatientInfoModal(true);
    } catch (err) {
      console.error('Error fetching patient info:', err);
      setError('Failed to load patient information');
    } finally {
      setLoadingPatientData(false);
    }
  };

  const closePatientInfoModal = () => {
    setShowPatientInfoModal(false);
    setPatientFullData(null);
    setPatientAppointments([]);
    setPatientTeethCharts([]);
    setPatientLabRequests([]);
  };

  // Download chart as PDF
  const downloadChartAsPDF = (chart) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Header
    doc.setFontSize(20);
    doc.setTextColor(0, 119, 182);
    doc.text('Teeth Chart Report', pageWidth / 2, y, { align: 'center' });
    y += 10;
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(chart.title || 'Dental Chart', pageWidth / 2, y, { align: 'center' });
    y += 15;

    // Patient Info
    doc.setDrawColor(0, 119, 182);
    doc.line(15, y, pageWidth - 15, y);
    y += 10;

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text('Patient Name:', 15, y);
    doc.setTextColor(0);
    doc.text(patientFullData?.fullName || 'N/A', 55, y);
    doc.setTextColor(100);
    doc.text('National ID:', 110, y);
    doc.setTextColor(0);
    doc.text(patientFullData?.nationalId || 'N/A', 145, y);
    y += 8;

    doc.setTextColor(100);
    doc.text('Chart Date:', 15, y);
    doc.setTextColor(0);
    doc.text(formatDateForDisplay(chart.date || chart.createdAt), 55, y);
    doc.setTextColor(100);
    doc.text('Status:', 110, y);
    doc.setTextColor(0);
    doc.text(chart.isLocked ? 'Locked' : 'Active', 145, y);
    y += 15;

    // Teeth Section
    doc.setFontSize(14);
    doc.setTextColor(0, 119, 182);
    doc.text(`Teeth Documented (${chart.teeth?.length || 0})`, 15, y);
    y += 10;

    if (chart.teeth?.length > 0) {
      doc.setFontSize(10);
      chart.teeth.forEach((tooth, idx) => {
        if (y > 270) { doc.addPage(); y = 20; }
        
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(15, y - 5, pageWidth - 30, 25, 3, 3, 'F');
        
        doc.setTextColor(0, 119, 182);
        doc.setFont(undefined, 'bold');
        doc.text(`Tooth #${tooth.toothNumber}`, 20, y + 2);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0);
        doc.text(`Condition: ${tooth.condition || 'N/A'}`, 20, y + 10);
        doc.text(`Treatment: ${tooth.treatment || 'N/A'}`, 100, y + 10);
        if (tooth.notes) doc.text(`Notes: ${tooth.notes}`, 20, y + 18);
        y += 30;
      });
    } else {
      doc.setTextColor(100);
      doc.text('No teeth documented in this chart.', 15, y);
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, 290, { align: 'center' });

    doc.save(`teeth-chart-${patientFullData?.nationalId || 'patient'}-${Date.now()}.pdf`);
  };

  // Download sheet as PDF
  const downloadSheetAsPDF = (sheet) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Header
    doc.setFontSize(20);
    doc.setTextColor(0, 119, 182);
    doc.text('Treatment Sheet Report', pageWidth / 2, y, { align: 'center' });
    y += 10;
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(sheet.type || 'Treatment Sheet', pageWidth / 2, y, { align: 'center' });
    y += 15;

    // Patient Info
    doc.setDrawColor(0, 119, 182);
    doc.line(15, y, pageWidth - 15, y);
    y += 10;

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text('Patient Name:', 15, y);
    doc.setTextColor(0);
    doc.text(patientFullData?.fullName || 'N/A', 55, y);
    doc.setTextColor(100);
    doc.text('National ID:', 110, y);
    doc.setTextColor(0);
    doc.text(patientFullData?.nationalId || 'N/A', 145, y);
    y += 8;

    doc.setTextColor(100);
    doc.text('Date:', 15, y);
    doc.setTextColor(0);
    doc.text(formatDateForDisplay(sheet.createdAt), 55, y);
    y += 15;

    // Diagnosis
    doc.setFillColor(227, 242, 253);
    doc.roundedRect(15, y - 5, pageWidth - 30, 25, 3, 3, 'F');
    doc.setFontSize(12);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');
    doc.text('Diagnosis', 20, y + 2);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0);
    const diagnosisLines = doc.splitTextToSize(sheet.details?.diagnosis || 'N/A', pageWidth - 50);
    doc.text(diagnosisLines, 20, y + 12);
    y += 30 + (diagnosisLines.length - 1) * 5;

    // Treatment Plan
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setFillColor(232, 245, 233);
    doc.roundedRect(15, y - 5, pageWidth - 30, 25, 3, 3, 'F');
    doc.setFontSize(12);
    doc.setTextColor(56, 142, 60);
    doc.setFont(undefined, 'bold');
    doc.text('Treatment Plan', 20, y + 2);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0);
    const treatmentLines = doc.splitTextToSize(sheet.details?.treatmentPlan || 'N/A', pageWidth - 50);
    doc.text(treatmentLines, 20, y + 12);
    y += 30 + (treatmentLines.length - 1) * 5;

    // Notes
    if (sheet.details?.notes) {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFillColor(255, 248, 225);
      doc.roundedRect(15, y - 5, pageWidth - 30, 25, 3, 3, 'F');
      doc.setFontSize(12);
      doc.setTextColor(249, 168, 37);
      doc.setFont(undefined, 'bold');
      doc.text('Notes', 20, y + 2);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(0);
      const notesLines = doc.splitTextToSize(sheet.details.notes, pageWidth - 50);
      doc.text(notesLines, 20, y + 12);
      y += 30 + (notesLines.length - 1) * 5;
    }

    // Additional Details
    if (sheet.details) {
      const entries = Object.entries(sheet.details).filter(([key]) => !['diagnosis', 'treatmentPlan', 'notes'].includes(key));
      if (entries.length > 0) {
        if (y > 240) { doc.addPage(); y = 20; }
        y += 10;
        doc.setFontSize(14);
        doc.setTextColor(0, 119, 182);
        doc.text('Additional Details', 15, y);
        y += 10;

        for (const [key, value] of entries) {
          if (y > 270) { doc.addPage(); y = 20; }
          
          if (isComplexObject(value)) {
            doc.setFontSize(11);
            doc.setTextColor(0, 119, 182);
            doc.setFont(undefined, 'bold');
            doc.text(formatKeyName(key), 15, y);
            doc.setFont(undefined, 'normal');
            y += 6;
            
            const flatItems = flattenObjectForDisplay(value);
            doc.setFontSize(9);
            for (const item of flatItems) {
              if (y > 280) { doc.addPage(); y = 20; }
              doc.setTextColor(100);
              doc.text(`${item.key}:`, 20, y);
              doc.setTextColor(0);
              const valueLines = doc.splitTextToSize(item.value, pageWidth - 90);
              doc.text(valueLines, 80, y);
              y += 5 + (valueLines.length - 1) * 4;
            }
            y += 5;
          } else {
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`${formatKeyName(key)}:`, 15, y);
            doc.setTextColor(0);
            doc.text(formatSimpleValue(value), 70, y);
            y += 6;
          }
        }
      }
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, 290, { align: 'center' });

    doc.save(`treatment-sheet-${patientFullData?.nationalId || 'patient'}-${Date.now()}.pdf`);
  };



  const filteredReviews = () => {
    let reviews = reviewsTab === 'pending' ? pendingReviews : doneReviews;
    
    if (studentFilter) {
      reviews = reviews.filter(r => 
        r.studentName?.toLowerCase().includes(studentFilter.toLowerCase()) ||
        r.studentId?.name?.toLowerCase().includes(studentFilter.toLowerCase())
      );
    }
    
    if (procedureFilter) {
      reviews = reviews.filter(r => 
        r.procedureType?.toLowerCase().includes(procedureFilter.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      reviews = reviews.filter(r => r.status === statusFilter);
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      reviews = reviews.filter(r => {
        const reviewDate = parseFirestoreDate(reviewsTab === 'pending' ? r.submittedDate : r.reviewedDate || r.submittedDate);
        if (!reviewDate) return false;
        
        if (dateFilter === 'today') {
          return reviewDate >= today;
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return reviewDate >= weekAgo;
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return reviewDate >= monthAgo;
        }
        return true;
      });
    }
    
    return reviews;
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: colorPalette.text }}>Staff Members Dashboard</h1>
              <p className="mt-1" style={{ color: colorPalette.text, opacity: 0.7 }}>Welcome back, {user?.name || 'Supervisor'}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowSignatureModal(true)}
                className="flex items-center px-3 sm:px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005a8b] transition-colors text-sm"
              >
                <FaSignature className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Manage Signature</span>
                <span className="sm:hidden">Signature</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm p-4 sm:p-6"
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-lg" style={{ backgroundColor: `${colorPalette.primary}20` }}>
                <FaClipboardCheck className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: colorPalette.primary }} />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium" style={{ color: colorPalette.text }}>Total Reviews</p>
                <p className="text-xl sm:text-2xl font-bold" style={{ color: colorPalette.text }}>{analyticsData.totalReviews || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm p-4 sm:p-6"
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-lg" style={{ backgroundColor: `${colorPalette.secondary}20` }}>
                <FaCalendarAlt className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: colorPalette.secondary }} />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium" style={{ color: colorPalette.text }}>Pending Reviews</p>
                <p className="text-xl sm:text-2xl font-bold" style={{ color: colorPalette.text }}>{pendingReviews.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-4 sm:p-6"
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-lg" style={{ backgroundColor: `${colorPalette.accent}20` }}>
                <FaCheckCircle className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: colorPalette.accent }} />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium" style={{ color: colorPalette.text }}>Completed</p>
                <p className="text-xl sm:text-2xl font-bold" style={{ color: colorPalette.text }}>{doneReviews.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm p-4 sm:p-6"
          >
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-lg" style={{ backgroundColor: `${colorPalette.primary}20` }}>
                <FaCheck className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: colorPalette.primary }} />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium" style={{ color: colorPalette.text }}>Accepted</p>
                <p className="text-xl sm:text-2xl font-bold" style={{ color: colorPalette.text }}>
                  {analyticsData.statusDistribution?.accepted || 0}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="text-lg sm:text-xl font-semibold" style={{ color: colorPalette.text }}>Reviews</h2>
              <div className="flex items-center space-x-4">
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setReviewsTab('pending')}
                    className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                      reviewsTab === 'pending'
                        ? 'bg-white shadow-sm'
                        : 'hover:bg-gray-200'
                    }`}
                    style={{
                      color: reviewsTab === 'pending' ? colorPalette.primary : colorPalette.text
                    }}
                  >
                    <span className="hidden sm:inline">Pending ({pendingReviews.length})</span>
                    <span className="sm:hidden">Pending</span>
                  </button>
                  <button
                    onClick={() => setReviewsTab('done')}
                    className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                      reviewsTab === 'done'
                        ? 'bg-white shadow-sm'
                        : 'hover:bg-gray-200'
                    }`}
                    style={{
                      color: reviewsTab === 'done' ? colorPalette.primary : colorPalette.text
                    }}
                  >
                    <span className="hidden sm:inline">Completed ({doneReviews.length})</span>
                    <span className="sm:hidden">Done</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colorPalette.text }}>Search Student</label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search by student name..."
                    value={studentFilter}
                    onChange={(e) => setStudentFilter(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colorPalette.text }}>Procedure Type</label>
                <select
                  value={procedureFilter}
                  onChange={(e) => setProcedureFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                >
                  <option value="">All Procedures</option>
                  <option value="Operative">Operative</option>
                  <option value="Fixed Prosthodontics">Fixed Prosthodontics</option>
                  <option value="Removable Prosthodontics">Removable Prosthodontics</option>
                  <option value="Endodontics">Endodontics</option>
                  <option value="Periodontics">Periodontics</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colorPalette.text }}>Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="denied">Declined</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colorPalette.text }}>Date</label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </select>
              </div>
            </div>
          </div>

          {/* Reviews Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: colorPalette.text, opacity: 0.7 }}>
                    Date
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: colorPalette.text, opacity: 0.7 }}>
                    Patient
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: colorPalette.text, opacity: 0.7 }}>
                    Student
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: colorPalette.text, opacity: 0.7 }}>
                    Procedure
                  </th>
                  {reviewsTab === 'done' && (
                    <>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: colorPalette.text, opacity: 0.7 }}>
                        Status
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: colorPalette.text, opacity: 0.7 }}>
                        Supervisor
                      </th>
                    </>
                  )}
                  {reviewsTab === 'pending' && (
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: colorPalette.text, opacity: 0.7 }}>
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReviews().length === 0 ? (
                  <tr>
                    <td colSpan={reviewsTab === 'done' ? 6 : 5} className="px-3 sm:px-6 py-12 text-center text-gray-500">
                      No reviews found
                    </td>
                  </tr>
                ) : (
                  filteredReviews().map((review) => (
                    <motion.tr
                      key={review.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleReviewSelect(review)}
                    >
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium" style={{ color: colorPalette.text }}>
                        {formatDateForDisplay(reviewsTab === 'pending' ? review.submittedDate : review.reviewedDate || review.submittedDate)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm" style={{ color: colorPalette.text, opacity: 0.7 }}>
                        <div className="flex flex-col space-y-1">
                          <span className="truncate max-w-[120px] sm:max-w-none">{review.patientId?.fullName || 'N/A'}</span>
                          {patientNationalId(review.patientId) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/supervisor/patient/${patientNationalId(review.patientId)}`);
                              }}
                              className="text-xs text-[#0077B6] hover:text-[#005a8b] hover:underline flex items-center font-medium"
                            >
                              <FaExternalLinkAlt className="mr-1 h-3 w-3" />
                              <span className="hidden sm:inline">View Patient Info</span>
                              <span className="sm:hidden">View</span>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm truncate max-w-[120px] sm:max-w-none" style={{ color: colorPalette.text, opacity: 0.7 }}>{review.studentName || 'N/A'}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm truncate max-w-[100px] sm:max-w-none" style={{ color: colorPalette.text, opacity: 0.7 }}>{review.procedureType || 'N/A'}</td>
                      {reviewsTab === 'done' && (
                        <>
                          <td className="px-3 sm:px-6 py-3 sm:py-4">
                            <span
                              className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                              style={{
                                backgroundColor: review.status === 'accepted' ? `${colorPalette.accent}20` : '#fee2e2',
                                color: review.status === 'accepted' ? colorPalette.accent : '#b91c1c'
                              }}
                            >
                              {review.status}
                            </span>
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm truncate max-w-[100px] sm:max-w-none" style={{ color: colorPalette.text, opacity: 0.7 }}>{review.supervisorName || 'N/A'}</td>
                        </>
                      )}
                      {reviewsTab === 'pending' && (
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm">
                          <button
                            onClick={() => handleReviewSelect(review)}
                            className="text-[#0077B6] hover:text-[#005a8b] hover:underline"
                          >
                            Review
                          </button>
                        </td>
                      )}
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* Review Detail Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden mx-2 sm:mx-0"
          >
            {/* Modal Header */}
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <FaClipboardCheck className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">Review Details</h2>
                    <p className="text-sm text-gray-600">
                      {selectedReview.procedureType || 'Dental Procedure'} • {selectedReview.studentName || 'Student'}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setSelectedReview(null);
                    setStepStatuses({});
                  }} 
                  className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Quick Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <div className="flex items-center">
                    <FaUserMd className="h-4 w-4 text-blue-600 mr-2" />
                    <div>
                      <p className="text-xs text-gray-600">Patient</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {selectedReview.patientId?.fullName || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="flex items-center">
                    <FaUserGraduate className="h-4 w-4 text-green-600 mr-2" />
                    <div>
                      <p className="text-xs text-gray-600">Student</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {selectedReview.studentName || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <div className="flex items-center">
                    <FaTooth className="h-4 w-4 text-purple-600 mr-2" />
                    <div>
                      <p className="text-xs text-gray-600">Procedure</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {selectedReview.procedureType || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <div className="flex items-center">
                    <FaCalendarAlt className="h-4 w-4 text-yellow-600 mr-2" />
                    <div>
                      <p className="text-xs text-gray-600">Date</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDateForDisplay(selectedReview.submittedDate)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setModalTab('details')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      modalTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Patient Details
                  </button>
                  <button
                    onClick={() => setModalTab('review')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      modalTab === 'review'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Review
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-[300px]">
                {modalTab === 'details' && (
                  <div className="space-y-4">
                    {/* Patient Details */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <FaUserMd className="h-4 w-4 text-blue-600 mr-2" />
                        Patient Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-600">Full Name</p>
                          <p className="text-sm font-medium text-gray-900">{selectedReview.patientId?.fullName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">National ID</p>
                          <p className="text-sm font-medium text-gray-900">{patientNationalId(selectedReview.patientId) || 'N/A'}</p>
                        </div>
                      </div>
                      {patientNationalId(selectedReview.patientId) && (
                        <div className="mt-3">
                          <button
                            onClick={() => navigate(`/supervisor/patient/${patientNationalId(selectedReview.patientId)}`)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-lg hover:from-[#005a8b] hover:to-[#1a9a94] transition-all duration-200 shadow-sm hover:shadow-md text-sm font-medium"
                          >
                            <FaExternalLinkAlt className="h-3 w-3" />
                            View Full Patient Info
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Review Details */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <FaCalendarAlt className="h-4 w-4 text-green-600 mr-2" />
                        Review Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-600">Student Comment</p>
                          <p className="text-sm text-gray-900 bg-white p-3 rounded border">
                            {selectedReview.comment || 'No comment provided'}
                          </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-600">Student ID</p>
                            <p className="text-sm font-medium text-gray-900">{selectedReview.studentId || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Submission Date</p>
                            <p className="text-sm font-medium text-gray-900">
                              {formatDateTimeForDisplay(selectedReview.submittedDate)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {modalTab === 'review' && selectedReview.status === 'pending' && (
                  <div className="space-y-4">
                    {/* Procedure Steps Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <FaListAlt className="h-4 w-4 text-purple-600 mr-2" />
                        Procedure Steps
                      </h3>
                      <div className="bg-white p-4 rounded border">
                        <ReviewStepsDisplay
                          reviewSteps={selectedReview.reviewSteps || []}
                          procedureType={selectedReview.procedureType}
                          onStepStatusChange={handleStepStatusChange}
                          isSupervisorMode={true}
                          stepStatuses={stepStatuses}
                        />
                      </div>
                    </div>

                    {/* Comment Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Add Comment</h3>
                      <textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                        placeholder="Add feedback for the student..."
                      />
                    </div>

                    {/* Signature Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Staff Member Signature</h3>
                      <div className="flex items-center space-x-4">
                        {reviewForm.supervisorSignature ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-32 h-16 border border-gray-300 rounded bg-white flex items-center justify-center">
                              <img 
                                src={reviewForm.supervisorSignature} 
                                alt="Signature" 
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                            <button
                              onClick={() => setReviewForm({ ...reviewForm, supervisorSignature: null })}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setShowSignatureModal(true)}
                              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                              <FaSignature className="mr-2 h-4 w-4" />
                              Add Signature
                            </button>
                            {savedSignature && (
                              <button
                                onClick={() => setReviewForm({ ...reviewForm, supervisorSignature: savedSignature })}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                              >
                                Use Saved
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                      <button
                        onClick={() => setSelectedReview(null)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleReviewSubmit(selectedReview.id, 'denied')}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                      >
                        <FaTimes className="inline mr-2 h-3 w-3" />
                        Decline
                      </button>
                      <button
                        onClick={() => handleReviewSubmit(selectedReview.id, 'accepted')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <FaCheck className="inline mr-2 h-3 w-3" />
                        Accept
                      </button>
                    </div>
                  </div>
                )}

                {modalTab === 'review' && selectedReview.status !== 'pending' && (
                  <div className="space-y-4">
                    {/* Procedure Steps Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <FaListAlt className="h-4 w-4 text-purple-600 mr-2" />
                        Procedure Steps
                      </h3>
                      <div className="bg-white p-4 rounded border">
                        <ReviewStepsDisplay
                          reviewSteps={selectedReview.reviewSteps || []}
                          procedureType={selectedReview.procedureType}
                          onStepStatusChange={null}
                          isSupervisorMode={false}
                          stepStatuses={selectedReview.stepStatuses || {}}
                        />
                      </div>
                    </div>

                    {/* Completed Review Info */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <FaCheckCircle className="h-4 w-4 text-green-600 mr-2" />
                        Completed Review
                      </h3>
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-600">Supervisor</p>
                            <p className="text-sm font-medium text-gray-900">{selectedReview.supervisorName || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Reviewed Date</p>
                            <p className="text-sm font-medium text-gray-900">
                              {formatDateTimeForDisplay(selectedReview.reviewedDate)}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Comment</p>
                          <p className="text-sm text-gray-900 bg-white p-3 rounded border">
                            {selectedReview.comment || 'No comment provided'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Status</p>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              selectedReview.status === 'accepted'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {selectedReview.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-0"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Manage Signature</h2>
                <button
                  onClick={() => setShowSignatureModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <SignatureManager
                initialSignature={savedSignature}
                onSignatureSelect={(signature) => {
                  setSavedSignature(signature);
                  setTimeout(() => setShowSignatureModal(false), 1500);
                }}
              />
            </div>
          </motion.div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          message={successMessage}
        />
      )}

      {/* Patient Full Info Modal */}
      {showPatientInfoModal && patientFullData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden mx-2 sm:mx-0"
          >
            {/* Modal Header */}
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#0077B6] to-[#20B2AA]">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-white/20">
                    <FaUser className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">{patientFullData.fullName}</h2>
                    <p className="text-sm text-white/80">ID: {patientFullData.nationalId}</p>
                  </div>
                </div>
                <button 
                  onClick={closePatientInfoModal}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors"
                >
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 bg-gray-50 px-4 sm:px-6 overflow-x-auto">
              <nav className="flex space-x-1 sm:space-x-2 py-2" style={{ minWidth: 'max-content' }}>
                {[
                  { id: 'overview', label: 'Overview', icon: FaUser },
                  { id: 'medical', label: 'Medical Info', icon: FaNotesMedical },
                  { id: 'appointments', label: 'Appointments', icon: FaCalendarAlt },
                  { id: 'teethCharts', label: 'Teeth Charts', icon: FaTooth },
                  { id: 'labRequests', label: 'Lab Requests', icon: FaFlask },
                  { id: 'treatmentSheets', label: 'Treatment Sheets', icon: FaClipboardList },
                  { id: 'consent', label: 'Consent', icon: FaFileSignature },
                  { id: 'gallery', label: 'Gallery', icon: FaImages },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setPatientInfoTab(tab.id)}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                      patientInfoTab === tab.id
                        ? 'bg-[#0077B6] text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <tab.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              {/* Overview Tab */}
              {patientInfoTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                    <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <FaIdCard className="h-4 w-4" /> Personal Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Full Name</span>
                        <span className="text-sm font-medium text-gray-900">{patientFullData.fullName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">National ID</span>
                        <span className="text-sm font-medium text-gray-900">{patientFullData.nationalId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Serial Number</span>
                        <span className="text-sm font-medium text-gray-900">{patientFullData.serialNumber || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                    <h3 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <FaVenusMars className="h-4 w-4" /> Demographics
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Gender</span>
                        <span className="text-sm font-medium text-gray-900 capitalize">{patientFullData.gender || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Age</span>
                        <span className="text-sm font-medium text-gray-900">{patientFullData.age || 'N/A'} years</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Occupation</span>
                        <span className="text-sm font-medium text-gray-900">{patientFullData.occupation || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
                    <h3 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <FaPhone className="h-4 w-4" /> Contact Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Phone</span>
                        <span className="text-sm font-medium text-gray-900">{patientFullData.phoneNumber || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Address</span>
                        <span className="text-sm font-medium text-gray-900 text-right max-w-[200px] truncate">{patientFullData.address || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
                    <h3 className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2">
                      <FaClipboardCheck className="h-4 w-4" /> Summary
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Appointments</span>
                        <span className="text-sm font-medium text-gray-900">{patientAppointments.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Teeth Charts</span>
                        <span className="text-sm font-medium text-gray-900">{patientTeethCharts.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Lab Requests</span>
                        <span className="text-sm font-medium text-gray-900">{patientLabRequests.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">Treatment Sheets</span>
                        <span className="text-sm font-medium text-gray-900">{patientFullData.treatmentSheets?.length || 0}</span>
                      </div>
                    </div>
                  </div>
                  
                  {patientFullData.notes && (
                    <div className="md:col-span-2 bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                      <h3 className="text-sm font-semibold text-yellow-800 mb-2">Notes</h3>
                      <p className="text-sm text-gray-700">{patientFullData.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Medical Info Tab */}
              {patientInfoTab === 'medical' && (
                <div className="space-y-4">
                  <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                    <h3 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <FaNotesMedical className="h-4 w-4" /> Chief Complaint
                    </h3>
                    <p className="text-sm text-gray-700 bg-white p-3 rounded-lg">
                      {patientFullData.medicalInfo?.chiefComplaint || 'No chief complaint recorded'}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                      <h3 className="text-sm font-semibold text-blue-800 mb-3">Chronic Diseases</h3>
                      {patientFullData.medicalInfo?.chronicDiseases?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {patientFullData.medicalInfo.chronicDiseases.map((disease, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {disease}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No chronic diseases recorded</p>
                      )}
                      {patientFullData.medicalInfo?.otherChronicDisease && (
                        <p className="mt-2 text-sm text-gray-600">
                          Other: {patientFullData.medicalInfo.otherChronicDisease}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                      <h3 className="text-sm font-semibold text-purple-800 mb-3">Current Medications</h3>
                      <p className="text-sm text-gray-700 bg-white p-3 rounded-lg">
                        {patientFullData.medicalInfo?.currentMedications || 'No medications recorded'}
                      </p>
                    </div>
                    
                    <div className="md:col-span-2 bg-green-50 p-4 rounded-xl border border-green-200">
                      <h3 className="text-sm font-semibold text-green-800 mb-3">Recent Surgical Procedures</h3>
                      <p className="text-sm text-gray-700 bg-white p-3 rounded-lg">
                        {patientFullData.medicalInfo?.recentSurgicalProcedures || 'No recent surgical procedures recorded'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Appointments Tab */}
              {patientInfoTab === 'appointments' && (
                <div>
                  {patientAppointments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FaCalendarAlt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No appointments found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {patientAppointments.slice(0, 10).map((apt, idx) => (
                        <div key={apt.id || idx} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">{apt.type || 'General Appointment'}</p>
                              <p className="text-sm text-gray-600">{formatDateForDisplay(apt.date)} at {apt.time || 'N/A'}</p>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              apt.status === 'completed' ? 'bg-green-100 text-green-800' :
                              apt.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {apt.status || 'pending'}
                            </span>
                          </div>
                          {apt.notes && <p className="text-sm text-gray-500 mt-2">{apt.notes}</p>}
                        </div>
                      ))}
                      {patientAppointments.length > 10 && (
                        <p className="text-center text-sm text-gray-500">
                          Showing 10 of {patientAppointments.length} appointments
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Teeth Charts Tab */}
              {patientInfoTab === 'teethCharts' && (
                <div>
                  {patientTeethCharts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FaTooth className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No teeth charts found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {patientTeethCharts.map((chart, idx) => (
                        <div key={chart.id || idx} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">{chart.title || 'Teeth Chart'}</p>
                              <p className="text-sm text-gray-600">{formatDateForDisplay(chart.date || chart.createdAt)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${chart.isLocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                {chart.isLocked ? 'Locked' : 'Active'}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mt-2">
                            {chart.teeth?.length || 0} teeth documented
                          </p>
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => setSelectedChart(chart)}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <FaEye /> View Details
                            </button>
                            <button
                              onClick={() => downloadChartAsPDF(chart)}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              <FaDownload /> Download PDF
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Lab Requests Tab */}
              {patientInfoTab === 'labRequests' && (
                <div>
                  {patientLabRequests.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FaFlask className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No lab requests found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {patientLabRequests.map((req, idx) => (
                        <div key={req.id || idx} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">{req.labType || 'Lab Request'}</p>
                              <p className="text-sm text-gray-600">{formatDateForDisplay(req.submitDate || req.createdAt)}</p>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              req.status === 'completed' ? 'bg-green-100 text-green-800' :
                              req.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                              req.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {req.status || 'pending'}
                            </span>
                          </div>
                          {req.notes && <p className="text-sm text-gray-500 mt-2">{req.notes}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Treatment Sheets Tab */}
              {patientInfoTab === 'treatmentSheets' && (
                <div>
                  {(!patientFullData.treatmentSheets || patientFullData.treatmentSheets.length === 0) ? (
                    <div className="text-center py-8 text-gray-500">
                      <FaClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No treatment sheets found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {patientFullData.treatmentSheets.map((sheet, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="font-medium text-gray-900">{sheet.type}</p>
                              <p className="text-sm text-gray-600">{formatDateForDisplay(sheet.createdAt)}</p>
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-500">Diagnosis:</span>
                              <span className="ml-2 text-gray-700">{sheet.details?.diagnosis || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Treatment Plan:</span>
                              <span className="ml-2 text-gray-700">{sheet.details?.treatmentPlan || 'N/A'}</span>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => setSelectedSheet(sheet)}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <FaEye /> View Details
                            </button>
                            <button
                              onClick={() => downloadSheetAsPDF(sheet)}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              <FaDownload /> Download PDF
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Consent Tab */}
              {patientInfoTab === 'consent' && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-xl border ${patientFullData.consent?.isSigned ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <FaFileSignature className={`h-6 w-6 ${patientFullData.consent?.isSigned ? 'text-green-600' : 'text-yellow-600'}`} />
                      <div>
                        <h3 className="font-semibold text-gray-900">Consent Status</h3>
                        <p className={`text-sm ${patientFullData.consent?.isSigned ? 'text-green-700' : 'text-yellow-700'}`}>
                          {patientFullData.consent?.isSigned ? 'Patient has signed the consent form' : 'Consent not yet signed'}
                        </p>
                      </div>
                    </div>
                    
                    {patientFullData.consent?.isSigned && (
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-500">Signed At</p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatDateTimeForDisplay(patientFullData.consent.signedAt)}
                          </p>
                        </div>
                        {patientFullData.consent.signatureText && (
                          <div>
                            <p className="text-xs text-gray-500">Signature Text</p>
                            <p className="text-sm font-medium text-gray-900">{patientFullData.consent.signatureText}</p>
                          </div>
                        )}
                        {patientFullData.consent.signatureImage && (
                          <div>
                            <p className="text-xs text-gray-500 mb-2">Signature</p>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 inline-block">
                              <img 
                                src={patientFullData.consent.signatureImage} 
                                alt="Patient Signature" 
                                className="max-h-20 object-contain"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Gallery Tab */}
              {patientInfoTab === 'gallery' && (
                <div>
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FaFileMedical className="h-4 w-4 text-blue-600" /> X-Ray Images
                    </h3>
                    {(!patientFullData.xrays || patientFullData.xrays.length === 0) ? (
                      <div className="text-center py-6 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">No X-ray images</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {patientFullData.xrays.map((xray, idx) => (
                          <div key={idx} className="relative group aspect-square">
                            <img 
                              src={xray.url} 
                              alt={`X-ray ${idx + 1}`}
                              className="w-full h-full object-cover rounded-lg border border-gray-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-end">
                              <div className="p-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                <p className="text-xs">{formatDateForDisplay(xray.date)}</p>
                              </div>

                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FaImages className="h-4 w-4 text-purple-600" /> Gallery Images
                    </h3>
                    {(!patientFullData.galleryImages || patientFullData.galleryImages.length === 0) ? (
                      <div className="text-center py-6 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">No gallery images</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {patientFullData.galleryImages.map((img, idx) => (
                          <div key={idx} className="relative group aspect-square">
                            <img 
                              src={img.url} 
                              alt={`Gallery ${idx + 1}`}
                              className="w-full h-full object-cover rounded-lg border border-gray-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-end">
                              <div className="p-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                <p className="text-xs">{formatDateForDisplay(img.date)}</p>
                                {img.note && <p className="text-xs truncate">{img.note}</p>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Teeth Chart Detail Modal */}
      {selectedChart && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl"
          >
            <div className="bg-gradient-to-r from-[#0077B6] to-[#00B4D8] p-5 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedChart.title || 'Teeth Chart'}</h2>
                <p className="text-white/80 text-sm">{formatDateForDisplay(selectedChart.date || selectedChart.createdAt)}</p>
              </div>
              <button onClick={() => setSelectedChart(null)} className="text-white/80 hover:text-white p-2">
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1 rounded-full text-sm ${selectedChart.isLocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                  {selectedChart.isLocked ? 'Locked' : 'Active'}
                </span>
                <button
                  onClick={() => downloadChartAsPDF(selectedChart)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <FaDownload /> Download
                </button>
              </div>
              
              <h3 className="font-semibold text-gray-900 mb-3">Teeth Documented ({selectedChart.teeth?.length || 0})</h3>
              {selectedChart.teeth?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedChart.teeth.map((tooth, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FaTooth className="text-[#0077B6]" />
                        <span className="font-semibold">Tooth #{tooth.toothNumber}</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-500">Condition:</span> <span className="text-gray-900">{tooth.condition || 'N/A'}</span></p>
                        <p><span className="text-gray-500">Treatment:</span> <span className="text-gray-900">{tooth.treatment || 'N/A'}</span></p>
                        {tooth.notes && <p><span className="text-gray-500">Notes:</span> <span className="text-gray-900">{tooth.notes}</span></p>}
                        {tooth.surface && <p><span className="text-gray-500">Surface:</span> <span className="text-gray-900">{tooth.surface}</span></p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <FaTooth className="mx-auto text-4xl text-gray-300 mb-2" />
                  <p>No teeth documented in this chart</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Treatment Sheet Detail Modal */}
      {selectedSheet && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl"
          >
            <div className="bg-gradient-to-r from-[#0077B6] to-[#00B4D8] p-5 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedSheet.type || 'Treatment Sheet'}</h2>
                <p className="text-white/80 text-sm">{formatDateForDisplay(selectedSheet.createdAt)}</p>
              </div>
              <button onClick={() => setSelectedSheet(null)} className="text-white/80 hover:text-white p-2">
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => downloadSheetAsPDF(selectedSheet)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <FaDownload /> Download
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Diagnosis</h4>
                  <p className="text-gray-700">{selectedSheet.details?.diagnosis || 'N/A'}</p>
                </div>
                
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-2">Treatment Plan</h4>
                  <p className="text-gray-700">{selectedSheet.details?.treatmentPlan || 'N/A'}</p>
                </div>
                
                {selectedSheet.details?.notes && (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <h4 className="font-semibold text-yellow-900 mb-2">Notes</h4>
                    <p className="text-gray-700">{selectedSheet.details.notes}</p>
                  </div>
                )}
                
                {/* Show all other details */}
                {selectedSheet.details && Object.entries(selectedSheet.details)
                  .filter(([key]) => !['diagnosis', 'treatmentPlan', 'notes'].includes(key))
                  .length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Additional Details</h4>
                    <div className="space-y-4">
                      {Object.entries(selectedSheet.details)
                        .filter(([key]) => !['diagnosis', 'treatmentPlan', 'notes'].includes(key))
                        .map(([key, value]) => {
                          if (isComplexObject(value)) {
                            const flatItems = flattenObjectForDisplay(value);
                            return (
                              <div key={key} className="bg-white rounded-lg p-4 border border-gray-200">
                                <h5 className="font-medium text-[#0077B6] mb-3 pb-2 border-b border-gray-100">{formatKeyName(key)}</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {flatItems.map((item, idx) => (
                                    <div key={idx} className="flex flex-col py-1">
                                      <span className="text-xs text-gray-500">{item.key}</span>
                                      <span className="text-sm text-gray-900">{item.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={key} className="bg-white rounded p-3 border border-gray-100">
                              <p className="text-xs text-gray-500">{formatKeyName(key)}</p>
                              <p className="text-sm text-gray-900 font-medium">
                                {formatSimpleValue(value)}
                              </p>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

    </div>
  );
};

export default SupervisorDashboard;